import { useContext, useState } from "react";
import { ButtonDS } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import {
  Field,
  inputStyle,
  ModificationModal,
  modalDescStyle,
  modalTitleStyle,
  SummaryRow,
  summaryBoxStyle,
} from "./shared";

interface AddressChangeFlowProps {
  onClose: () => void;
  onSubmitted: () => void;
}

type Step = "form" | "confirm";

const AddressChangeFlow = ({ onClose, onSubmitted }: AddressChangeFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [floorDoor, setFloorDoor] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const zipcodeValid = /^\d{5}$/.test(zipcode.trim());
  const formValid =
    street.trim() !== "" &&
    number.trim() !== "" &&
    city.trim() !== "" &&
    province.trim() !== "" &&
    zipcodeValid;

  const t = (key: string) => translate(`newDashboard.modifications.address.${key}`);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const repository = new HttpLeadRepository(leadId, token);
      await repository.postModificationRequest("ADDRESS_CHANGE", {
        street: street.trim(),
        number: number.trim(),
        floorDoor: floorDoor.trim() !== "" ? floorDoor.trim() : null,
        city: city.trim(),
        province: province.trim(),
        zipcode: zipcode.trim(),
      });
      onSubmitted();
      setNotification({
        message: translate("newDashboard.modifications.common.successToast"),
        type: "success",
        time: 6,
      });
      onClose();
    } catch (error: any) {
      const key =
        error?.status === 409
          ? "newDashboard.modifications.errors.alreadyInProgress"
          : "newDashboard.modifications.errors.generic";
      setNotification({ message: translate(key), type: "error", time: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  const addressLine = `${street.trim()} ${number.trim()}${
    floorDoor.trim() !== "" ? `, ${floorDoor.trim()}` : ""
  }`;

  return (
    <ModificationModal onClose={onClose}>
      {step === "form" && (
        <div>
          <h2 style={modalTitleStyle}>{t("title")}</h2>
          <p style={modalDescStyle}>{t("desc")}</p>
          <Field
            label={t("street")}
            error={showErrors && street.trim() === "" ? t("required") : undefined}
          >
            <input style={inputStyle} value={street} onChange={(e) => setStreet(e.target.value)} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field
              label={t("number")}
              error={showErrors && number.trim() === "" ? t("required") : undefined}
            >
              <input style={inputStyle} value={number} onChange={(e) => setNumber(e.target.value)} />
            </Field>
            <Field label={t("floorDoor")}>
              <input
                style={inputStyle}
                value={floorDoor}
                onChange={(e) => setFloorDoor(e.target.value)}
                placeholder={t("optional")}
              />
            </Field>
          </div>
          <Field
            label={t("city")}
            error={showErrors && city.trim() === "" ? t("required") : undefined}
          >
            <input style={inputStyle} value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field
              label={t("province")}
              error={showErrors && province.trim() === "" ? t("required") : undefined}
            >
              <input
                style={inputStyle}
                value={province}
                onChange={(e) => setProvince(e.target.value)}
              />
            </Field>
            <Field
              label={t("zipcode")}
              error={showErrors && !zipcodeValid ? t("zipcodeInvalid") : undefined}
            >
              <input
                style={inputStyle}
                value={zipcode}
                inputMode="numeric"
                maxLength={5}
                onChange={(e) => setZipcode(e.target.value)}
              />
            </Field>
          </div>
          <ButtonDS
            label={translate("newDashboard.modifications.common.continue")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={() => {
              if (!formValid) return setShowErrors(true);
              setStep("confirm");
            }}
          />
        </div>
      )}

      {step === "confirm" && (
        <div>
          <h2 style={modalTitleStyle}>{t("confirmTitle")}</h2>
          <p style={modalDescStyle}>
            {translate("newDashboard.modifications.common.confirmSignatureDesc")}
          </p>
          <div style={summaryBoxStyle}>
            <SummaryRow label={t("summaryAddress")} value={addressLine} />
            <SummaryRow label={t("city")} value={city.trim()} />
            <SummaryRow label={t("province")} value={province.trim()} />
            <SummaryRow label={t("zipcode")} value={zipcode.trim()} />
          </div>
          <ButtonDS
            label={translate("newDashboard.modifications.common.confirmAndSign")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={submit}
            disabled={submitting}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("form")}
          />
        </div>
      )}

    </ModificationModal>
  );
};

export default AddressChangeFlow;
